<template>
  <div id="choose-prompt">
    <b-container fluid class="background">
      <b-row class="pt-4 pb-2 title top-nav-row responsive-padding">
        <b-col class="px-0">
          <a
            class="strip top-back-link"
            @click="handleBack()"
            >&larr; Back</a
          >
        </b-col>
      </b-row>
      <b-row class="responsive-padding breadcrumb-row">
        <breadcrumb :items="items"></breadcrumb>
      </b-row>

      <section class="page-hero responsive-padding">
        <h1 class="page-title">Choose a Prompt</h1>
        <p class="page-subtitle">
          Select one LEQ prompt below to begin writing.
        </p>
      </section>

      <b-row
        class="white-container responsive-padding"
      >
        <whitecontainer id="module-white-container">
          <div class="prompt-actions-wrapper">
            <div class="prompt-actions">
              <b-button
                class="refresh-prompts-button"
                :disabled="!isAuthenticated || isRefreshing"
                :title="!isAuthenticated ? 'Sign in to unlock new prompts' : 'Refresh your prompt selection'"
                @click="refreshPrompts"
              >
                <span v-if="isRefreshing" class="button-content">
                  <b-spinner small class="loading-spinner"></b-spinner>
                  <span>Shuffling...</span>
                </span>
                <span v-else class="button-content">
                  <b-icon-arrow-repeat v-if="isAuthenticated" class="refresh-icon"></b-icon-arrow-repeat>
                  <span>Get New Prompts</span>
                </span>
              </b-button>
              <div v-if="!isAuthenticated" class="signin-prompt">
                <p class="signin-text">Sign in to unlock new prompts</p>
              </div>
            </div>
          </div>
          <transition-group name="prompt-fade" tag="div" class="prompt-list">
            <prompt
              v-for="prompt in prompts"
              :key="prompt.id"
              :prompt="prompt"
              class="prompt"
              id="prompt"
              :hover="true"
              :class="{
                active: currentPrompt === prompt.id
              }"
            ></prompt>
          </transition-group>
        </whitecontainer>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import whitecontainer from "../../components/writing_feature/WhiteContainer.vue";
import breadcrumb from "../../components/writing_feature/BreadCrumb.vue";
import prompt from "../../components/writing_feature/Prompt.vue";
import storeWriting from "@/store/writing.js";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { pushRoute } from "@/router/navigation";
import { useSubscriptionAccess } from "@/composables/useSubscriptionAccess";
import { trackEvent } from "@/services/analyticsService";

const PROMPT_BATCH_SIZE = 3;

function shuffle(list) {
  const array = [...list];
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

function pickPromptBatch(pool, count, previousIds = []) {
  const previousSet = new Set(previousIds);
  const uniquePool = Array.isArray(pool) ? pool : [];

  let candidates = uniquePool.filter(item => !previousSet.has(item.id));
  if (candidates.length < count) {
    candidates = uniquePool;
  }

  return shuffle(candidates).slice(0, count);
}

function selectVisiblePrompts(pool, previousIds = []) {
  const uniquePool = Array.isArray(pool) ? pool : [];
  return pickPromptBatch(uniquePool, PROMPT_BATCH_SIZE, previousIds);
}

export default {
  components: {
    whitecontainer,
    breadcrumb,
    prompt
  },
  setup() {
    const route = useRoute();
    if (!["Beginner", "Intermediate", "Expert"].includes(route.params.module)) {
      pushRoute("001");
    }

    const store = storeWriting;
    const promptPool = computed(() => store.state.prompts || []);
    const prompts = ref([]);
    const isRefreshing = ref(false);
    const { isAuthenticated, canViewAllPrompts } = useSubscriptionAccess();

    const loadVisiblePrompts = previousIds => {
      prompts.value = selectVisiblePrompts(
        promptPool.value,
        previousIds
      );
    };

    const refreshPrompts = async () => {
      if (!canViewAllPrompts.value || isRefreshing.value) {
        return;
      }

      const previousIds = prompts.value.map(item => item.id);
      isRefreshing.value = true;
      // Simulate a brief delay for better visual feedback
      await new Promise(resolve => setTimeout(resolve, 300));
      loadVisiblePrompts(previousIds);
      isRefreshing.value = false;
    };

    onMounted(() => {
      store.dispatch("unSetSelectedPrompt");
      window.scrollTo({ top: 0, behavior: "smooth" });
    });

    watch([promptPool, canViewAllPrompts], () => {
      loadVisiblePrompts();
    }, { immediate: true });

    const currentPrompt = computed(() => store.state.selectedPrompt);

    watch(currentPrompt, selectedId => {
      if (!selectedId) return;
      trackEvent("prompt_selected", {
        prompt_id: selectedId,
        module: route.params.module
      });
      pushRoute("003", { id: selectedId, module: route.params.module });
    });

    const items = [
      {
        text: "Essay Writing: LEQ"
      },
      {
        text: "Choose a Prompt",
        active: "yes"
      }
    ];
    const handleBack = () => {
      pushRoute("001");
    };
    return {
      items,
      prompts,
      currentPrompt,
      isAuthenticated,
      isRefreshing,
      refreshPrompts,
      handleBack
    };
  }
};
</script>
<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Manrope:wght@700&display=swap");
.active {
  opacity: 1;
}
#module-white-container {
  padding-top: 28px;
  padding-bottom: 10px;
}

#module-white-container :deep(.true-white-container) {
  min-height: auto;
}

.white-container {
  width: 100%;
  max-width: 1080px;
  margin: 0 auto !important;
  padding-left: 0.75rem !important;
  padding-right: 0.75rem !important;
}

.top-nav-row {
  margin-bottom: 2px;
}

.top-back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #8c30f5;
  background: transparent;
  font-size: 16px;
  font-weight: 700;
  color: #8c30f5;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease, transform 0.15s ease;
}

.top-back-link:hover {
  color: #8c30f5;
  border-color: #8c30f5;
  background: rgba(140, 48, 245, 0.08);
  box-shadow: 0 4px 10px rgba(140, 48, 245, 0.2);
  transform: translateY(-1px);
  text-decoration: none;
}

.top-back-link:active {
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(140, 48, 245, 0.16);
}

.breadcrumb-row {
  margin-bottom: 4px;
}

.page-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 16px;
}

.page-title {
  margin: 0;
  color: #171717;
  font-size: clamp(1.8rem, 2.4vw, 2.35rem);
  font-weight: 800;
  line-height: 1.06;
}

.page-subtitle {
  margin: 8px 0 0;
  max-width: 620px;
  color: #4b5563;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
}

.prompt-actions-wrapper {
  margin: 0 0 14px;
  padding: 0;
}

.prompt-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-wrap: nowrap;
  justify-content: flex-start;
}

.refresh-prompts-button {
  background: linear-gradient(135deg, #8c30f5 0%, #a855f5 100%);
  border: none;
  color: white;
  font-weight: 600;
  font-size: 14px;
  border-radius: 999px;
  padding: 10px 18px;
  height: auto;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(140, 48, 245, 0.25);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.refresh-prompts-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #7f24ea 0%, #9d4fdd 100%);
  box-shadow: 0 8px 20px rgba(140, 48, 245, 0.35);
  transform: translateY(-2px);
}

.refresh-prompts-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 4px 12px rgba(140, 48, 245, 0.25);
}

.refresh-prompts-button:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  border: 1px solid #e5e7eb;
  box-shadow: none;
  cursor: not-allowed;
}

.button-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 1;
}

.refresh-icon,
.lock-icon,
.loading-spinner {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.refresh-icon {
  transition: transform 0.3s ease;
}

.refresh-prompts-button:hover:not(:disabled) .refresh-icon {
  transform: rotate(180deg);
}

.loading-spinner {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.signin-prompt {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-height: auto;
  padding: 0;
}

.signin-text {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
}

.prompt-list {
  width: 100%;
}

.prompt {
  transition: 0.1s;
}

.prompt:nth-child(1) {
  background: var(--accent-blue-1, rgb(202, 226, 241, 0.5));
}
.prompt:nth-child(2) {
  background: var(--accent-green-1, rgba(204, 235, 165, 0.5));
  margin: 15px 0;
}
.prompt:nth-child(3) {
  background: var(--accent-yellow-1, rgba(245, 235, 187, 0.5));
}

.prompt:nth-child(1).active,
.prompt:nth-child(1):hover {
  background: var(--dark-accent-blue, #85cdf9);
}

.prompt:nth-child(2).active,
.prompt:nth-child(2):hover {
  background: var(--dark-accent-green, #9bd84f);
}

.prompt:nth-child(3).active,
.prompt:nth-child(3):hover {
  background: var(--dark-accent-yellow, #f5e07b);
}

.prompt.active,
.prompt:hover {
  color: white;
}

.prompt-fade-enter-active,
.prompt-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.prompt-fade-enter-from,
.prompt-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@media (max-width: 768px) {
  .prompt-actions-wrapper {
    margin-bottom: 16px;
  }

  .prompt-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .refresh-prompts-button {
    width: 100%;
    justify-content: center;
  }

  .signin-prompt {
    justify-content: center;
    text-align: center;
  }
}

@media (max-width: 991px) {
  .white-container {
    max-width: 100%;
  }
}
</style>
<style lang="sass" scoped src="@/assets/css/essayWriting.sass"></style>
